import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { SEOHead } from '@/components/SEOHead';
import { SectionNum, CtaPrimary, CtaGhost } from '@/components/Patterns';
import { PageMasthead } from '@/components/ui/PageMasthead';
import { PageNav } from '@/components/ui/PageNav';
import { MobileSectionNav } from '@/components/ui/MobileSectionNav';
import { useSectionSpy } from '@/hooks/useSectionSpy';

/* ── Delivery step index — ids + existing titles + numerals (copy-locked labels) ── */

const DELIVERY_STEPS = [
  { id: 'delivery-1', num: '01', label: 'Engagement Architecture' },
  { id: 'delivery-2', num: '02', label: 'Data Exchange Model' },
  { id: 'delivery-3', num: '03', label: 'Data Handling Environment' },
  { id: 'delivery-4', num: '04', label: 'Data Minimization Principle' },
  { id: 'delivery-5', num: '05', label: 'Data Retention & Deletion' },
  { id: 'delivery-6', num: '06', label: 'Confidentiality & NDA Structure' },
  { id: 'delivery-7', num: '07', label: 'Analytical Boundaries' },
  { id: 'delivery-8', num: '08', label: 'Artifact Delivery Model' },
  { id: 'delivery-9', num: '09', label: 'Engagement Scope Control' },
  { id: 'delivery-10', num: '10', label: 'Liability Framework' },
  { id: 'delivery-11', num: '11', label: 'Pricing Architecture' },
  { id: 'delivery-12', num: '12', label: 'Enterprise Compatibility' },
];

const DELIVERY_IDS = DELIVERY_STEPS.map((s) => s.id);

/* ── Local primitives (v2 memorandum styling) ── */

/* Affirmative list — em-dash marker in signal green, ink-2 text */
const BulletList = ({ items }) => (
  <ul className="space-y-2">
    {items.map((item) => (
      <li key={item} className="flex items-start gap-3">
        <span className="text-memo-signal mt-0.5" aria-hidden="true">—</span>
        <span className="text-[0.95rem] leading-relaxed" style={{ color: 'var(--memo-ink-2)' }}>{item}</span>
      </li>
    ))}
  </ul>
);

/* Exclusion list — de-emphasised dash, ink-2 text (what is NOT included/not done) */
const NotList = ({ items }) => (
  <ul className="space-y-2">
    {items.map((item) => (
      <li key={item} className="flex items-start gap-3">
        <span className="text-memo-faint mt-0.5" aria-hidden="true">—</span>
        <span className="text-[0.95rem] leading-relaxed" style={{ color: 'var(--memo-ink-2)' }}>{item}</span>
      </li>
    ))}
  </ul>
);

/* Hairline-ruled policy line (left border, marginal) */
const PolicyLine = ({ text }) => (
  <p className="pl-4 border-l border-memo-line text-[0.95rem] leading-relaxed" style={{ color: 'var(--memo-ink-2)' }}>{text}</p>
);

/* Mono section label (e.g. "2.1 Accepted Data Formats") */
const SubHeader = ({ title, muted }) => (
  <p className={`font-mono text-xs uppercase tracking-[0.14em] mb-3 ${muted ? 'text-memo-muted' : 'text-memo-signal'}`}>{title}</p>
);

/* Hairline-ruled memorandum ledger row (step / policy term) */
const LedgerRow = ({ num, label }) => (
  <div className="flex items-baseline gap-5 py-3 border-b border-memo-line last:border-0">
    <span className="font-mono text-xs text-memo-signal tracking-[0.18em] w-9 flex-shrink-0">{num}</span>
    <span className="text-[1rem] leading-snug" style={{ color: 'var(--memo-ink)' }}>{label}</span>
  </div>
);

/* Memorandum header: marginal section number + display title, hairline beneath.
   Toggleable (accordion) — WAI-ARIA pattern: heading wraps the toggle button,
   so the heading is a real h2 (outline hygiene) and the button is the control. */
const SectionHead = ({ num, title, isOpen, onToggle, children }) => (
  <div>
    <h2>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        className="w-full flex items-center gap-5 py-4 text-left group"
      >
        <SectionNum>{num}</SectionNum>
        <span className="font-display text-2xl md:text-3xl font-medium tracking-tight flex-1" style={{ color: 'var(--memo-ink)' }}>{title}</span>
        <ChevronDown
          size={18}
          aria-hidden="true"
          className={`text-memo-muted shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : 'rotate-0'}`}
        />
      </button>
    </h2>
    {isOpen && (
      <div className="pb-6">
        {children}
      </div>
    )}
  </div>
);

/* LEDGER_* content arrays (copy-locked strings) */

const LEDGER_ARCHITECTURE = [
  'Diagnostic Session (classification)',
  'Data Specification & Export Guidance',
  'Secure File Transfer',
  'Offline Analytical Processing',
  'Structured Artifact Delivery',
  'Optional Interpretation Session',
];

const DATA_FORMATS = ['CSV', 'XLSX', 'TXT', 'Standard WFM exports', 'Interval-level operational logs'];
const DATA_PLATFORMS = ['Genesys', 'NICE', 'Verint', 'Aspect', 'Calabrio', 'In-house systems', 'Any export-capable WFM platform'];
const TRANSFER_OPTIONS = [
  'Encrypted file transfer link',
  'Client-secured upload portal',
  'Encrypted archive via email',
  'Client-preferred secure transfer channel',
];
const PROCESSING_MODEL = [
  'Isolated analysis environment',
  'Non-production Linux system',
  'No active synchronization',
  'No persistent client data repository',
];
const NO_STORAGE = ['Public SaaS tools', 'Shared BI platforms', 'Cloud dashboards', 'Persistent collaborative sheets'];
const TOOLCHAIN = [
  'Structured statistical modeling',
  'Controlled scripting environment',
  'File-based computation',
  'Deterministic reproducibility',
];
const MINIMIZE_FOR = ['Structural classification', 'Signal decomposition', 'Exposure mapping'];
const NOT_REQUEST = ['Personal agent identifiers unless required', 'Sensitive HR data', 'Compensation data', 'Customer-level PII'];
const ANON_OPTIONS = ['ID anonymization is recommended', 'Hash-based pseudonymization accepted'];
const RETENTION = [
  'Data retained maximum 14 calendar days after artifact delivery',
  'Interim working files removed upon report finalization',
  'Backup artifacts excluded',
];
const DELETION = ['Written deletion confirmation issued', 'Processing environment cleared', 'No archival storage retained'];
const NDA_OPTIONS = ['Mutual NDA', 'Client-provided NDA'];
const CONFIDENTIAL_POSTURE = [
  'All client data treated confidential',
  'No benchmarking disclosure without explicit permission',
  'No derivative insights referencing identifiable structures',
];
const NDA_NOTS = ['Publish client names', 'Publish structural findings', 'Use data for marketing', 'Share case data without anonymization approval'];
const PROVIDE = ['Structural interpretation', 'Signal classification', 'Exposure quantification', 'Sensitivity mapping', 'Scenario modeling'];
const NOT_PROVIDE = ['Operational command authority', 'Workforce disciplinary decisions', 'HR policy enforcement', 'Vendor selection', 'System reconfiguration'];
const ARTIFACTS = [
  'Structured PDF analytical reports',
  'Tabular annex files',
  'Interval-mapped diagnostic tables',
  'Sensitivity maps',
  'Structural distortion diagrams',
];
const NOT_DELIVER = ['Live dashboards', 'Real-time monitoring tools', 'Ongoing SaaS analytics'];
const INCLUDES = ['Defined analytical boundary', 'Defined data perimeter', 'Defined artifact list', 'Defined timeline'];
const SCOPE_EXPANSION = ['Formal extension agreement', 'Additional data specification', 'Explicit boundary adjustment'];
const LIABILITY = [
  'Analytical services only',
  'No operational guarantees',
  'No financial performance guarantees',
  'No compliance guarantees',
  'No SLA performance guarantees',
];
const PRICING_FACTORS = ['Interval volume size', 'FTE scope', 'Data dimensionality', 'Analytical depth', 'Scenario modeling requirement'];
const DIAGNOSTIC_OUTCOME = ['Investigation complexity', 'Analytical pathway', 'Scope definition'];
const COMPATIBLE = ['Procurement review', 'Legal review', 'Data protection review', 'Information security review'];
const BECAUSE = ['No system access required', 'No persistent data storage', 'No operational integration', 'No production dependency'];

export function DeliveryModel() {
  const [openSection, setOpenSection] = useState(1);
  const toggle = (n) => setOpenSection(openSection === n ? null : n);
  // Top band of the viewport: sections pin there via scroll-margin-top (5.5rem),
  // so the collapsed accordion headers (short targets) register reliably.
  const activeId = useSectionSpy(DELIVERY_IDS, '0px 0px -80% 0px');
  return (
    <div style={{ backgroundColor: 'var(--memo-field)' }}>
      <SEOHead />

      {/* ── Compact masthead — title left, governed boundary frame right ── */}
      <PageMasthead
        wide
        field="page"
        fieldFocal="0.72,0.62"
        eyebrow="Process Reference"
        title="Delivery Model"
        intro="File-Based Analytical Engagement"
      >
        <div className="p-6 border border-memo-line-2 bg-memo-paper rounded max-w-sm">
          <div className="space-y-2">
            <PolicyLine text="We do not integrate into production systems." />
            <PolicyLine text="We do not require system credentials." />
            <PolicyLine text="We do not deploy software inside client infrastructure." />
          </div>
          <div aria-hidden="true" className="border-t border-memo-line my-4" />
          <p className="text-sm leading-relaxed" style={{ color: 'var(--memo-muted)' }}>
            All engagements are conducted through controlled file-based data exchange.
          </p>
        </div>
      </PageMasthead>

      {/* ── Delivery step index + accordions (lg: nav | content) ── */}
      <div className="container-site spine-shell lg:grid lg:grid-cols-[16rem_minmax(0,1fr)]">
        <PageNav items={DELIVERY_STEPS} activeId={activeId} label="Delivery model steps" />

        <div className="lg:min-w-0 lg:border-l lg:border-memo-line lg:pl-10">
          <MobileSectionNav items={DELIVERY_STEPS} activeId={activeId} label="Delivery model steps" />

      {/* ── 1. Engagement Architecture — hairline-ruled ledger, full width ── */}
      <section className="py-16 md:py-20 bg-memo-paper border-b border-memo-line">
        <div className="container-site">
          <div id="delivery-1" data-testid="delivery-section-1">
            <SectionHead num="01" title="Engagement Architecture" isOpen={openSection === 1} onToggle={() => toggle(1)}>
              <p className="mt-3 max-w-[58ch] leading-relaxed" style={{ color: 'var(--memo-ink-2)' }}>
                Every investigation follows a defined sequence:
              </p>
              <div className="mt-1">
                {LEDGER_ARCHITECTURE.map((step, i) => (
                  <LedgerRow key={step} num={String(i + 1).padStart(2, '0')} label={step} />
                ))}
              </div>
            </SectionHead>
            <div className="mt-7 space-y-2">
              <PolicyLine text="No system dependency." />
              <PolicyLine text="No API integrations." />
              <PolicyLine text="No operational footprint inside client environment." />
            </div>
          </div>
        </div>
      </section>

      {/* ── 2. Data Exchange Model — asymmetric two-column split ── */}
      <section className="py-16 md:py-20 border-b border-memo-line">
        <div className="container-site">
          <div id="delivery-2" data-testid="delivery-section-2">
            <SectionHead num="02" title="Data Exchange Model" isOpen={openSection === 2} onToggle={() => toggle(2)}>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-10 items-start">
              <div>
                <SubHeader title="2.1 Accepted Data Formats" />
                <BulletList items={DATA_FORMATS} />
                <p className="mt-4 text-[0.95rem] leading-relaxed" style={{ color: 'var(--memo-ink-2)' }}>We work with:</p>
                <BulletList items={DATA_PLATFORMS} />
                <div className="mt-6 space-y-2">
                  <PolicyLine text="System origin is irrelevant." />
                  <PolicyLine text="We require only structured interval-level data." />
                </div>
              </div>
              <div>
                <SubHeader title="2.2 Transfer Mechanism" muted />
                <p className="text-[0.95rem] leading-relaxed mb-3" style={{ color: 'var(--memo-ink-2)' }}>Data transfer options:</p>
                <BulletList items={TRANSFER_OPTIONS} />
                <div className="mt-6 space-y-2">
                  <PolicyLine text="No third-party SaaS routing beyond agreed transfer layer." />
                  <PolicyLine text="No public cloud dashboards." />
                </div>
              </div>
            </div>
        </SectionHead>
          </div>
</div>
      </section>

      {/* ── 3. Data Handling Environment — full-width memorandum frame ── */}
      <section className="py-16 md:py-20 border-b border-memo-line">
        <div className="container-site">
          <div id="delivery-3" data-testid="delivery-section-3">
            <SectionHead num="03" title="Data Handling Environment" isOpen={openSection === 3} onToggle={() => toggle(3)}>
            <div className="mt-8 border border-memo-line-2 bg-memo-paper rounded p-8">
              <SubHeader title="3.1 Analytical Processing Model" />
              <p className="text-[0.95rem] leading-relaxed mb-3" style={{ color: 'var(--memo-ink-2)' }}>All data is processed in:</p>
              <BulletList items={PROCESSING_MODEL} />
              <p className="mt-5 text-[0.95rem] leading-relaxed mb-3" style={{ color: 'var(--memo-ink-2)' }}>
                Processing is file-contained. No client data is stored in:
              </p>
              <NotList items={NO_STORAGE} />
              <div className="mt-7 pt-1">
                <SubHeader title="3.2 Analytical Toolchain" muted />
              </div>
              <p className="text-[0.95rem] leading-relaxed mb-3" style={{ color: 'var(--memo-ink-2)' }}>Primary environment:</p>
              <BulletList items={TOOLCHAIN} />
              <div className="mt-6 space-y-2">
                <PolicyLine text="No automated decision systems." />
                <PolicyLine text="No AI-generated operational directives." />
                <PolicyLine text="Interpretation is analytical, not automated." />
              </div>
            </div>
        </SectionHead>
          </div>
</div>
      </section>

      {/* ── 4. Data Minimization Principle — narrow passage + note frame ── */}
      <section className="py-16 md:py-20 bg-memo-paper border-b border-memo-line">
        <div className="container-site">
          <div id="delivery-4" data-testid="delivery-section-4">
            <SectionHead num="04" title="Data Minimization Principle" isOpen={openSection === 4} onToggle={() => toggle(4)}>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-10 items-start">
              <div>
                <p className="text-[0.95rem] leading-relaxed mb-3" style={{ color: 'var(--memo-ink-2)' }}>We request only data necessary for:</p>
                <BulletList items={MINIMIZE_FOR} />
                <p className="mt-6 text-[0.95rem] leading-relaxed mb-3" style={{ color: 'var(--memo-ink-2)' }}>We do not request:</p>
                <NotList items={NOT_REQUEST} />
              </div>
              <div className="p-7 border border-memo-line-2 bg-memo-field rounded">
                <SubHeader title="If agent-level data is required" />
                <BulletList items={ANON_OPTIONS} />
              </div>
            </div>
        </SectionHead>
          </div>
</div>
      </section>

      {/* ── 5. Data Retention & Deletion — paired ledgers ── */}
      <section className="py-16 md:py-20 border-b border-memo-line">
        <div className="container-site">
          <div id="delivery-5" data-testid="delivery-section-5">
            <SectionHead num="05" title="Data Retention & Deletion" isOpen={openSection === 5} onToggle={() => toggle(5)}>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-10 items-start">
              <div>
                <SubHeader title="5.1 Retention Policy" />
                <p className="text-[0.95rem] leading-relaxed mb-3" style={{ color: 'var(--memo-ink-2)' }}>Unless otherwise agreed:</p>
                <div className="border border-memo-line rounded overflow-hidden">
                  {RETENTION.map((line) => <p key={line} className="px-4 py-2.5 border-b border-memo-line last:border-0 text-[0.95rem] leading-snug" style={{ color: 'var(--memo-ink-2)' }}>{line}</p>)}
                </div>
              </div>
              <div>
                <SubHeader title="5.2 Deletion Confirmation" muted />
                <p className="text-[0.95rem] leading-relaxed mb-3" style={{ color: 'var(--memo-ink-2)' }}>Upon request:</p>
                <div className="border border-memo-line rounded overflow-hidden">
                  {DELETION.map((line) => <p key={line} className="px-4 py-2.5 border-b border-memo-line last:border-0 text-[0.95rem] leading-snug" style={{ color: 'var(--memo-ink-2)' }}>{line}</p>)}
                </div>
              </div>
            </div>
        </SectionHead>
          </div>
</div>
      </section>

      {/* ── 6. Confidentiality & NDA Structure — stacked editorial lists ── */}
      <section className="py-16 md:py-20 border-b border-memo-line">
        <div className="container-site">
          <div id="delivery-6" data-testid="delivery-section-6">
            <SectionHead num="06" title="Confidentiality & NDA Structure" isOpen={openSection === 6} onToggle={() => toggle(6)}>
            <div className="mt-8 space-y-8 max-w-[62ch]">
              <div>
                <p className="text-[0.95rem] leading-relaxed mb-3" style={{ color: 'var(--memo-ink-2)' }}>All engagements operate under:</p>
                <BulletList items={NDA_OPTIONS} />
              </div>
              <div>
                <p className="text-[0.95rem] leading-relaxed mb-3" style={{ color: 'var(--memo-ink-2)' }}>Default posture:</p>
                <BulletList items={CONFIDENTIAL_POSTURE} />
              </div>
              <div>
                <p className="text-[0.95rem] leading-relaxed mb-3" style={{ color: 'var(--memo-ink-2)' }}>We do not:</p>
                <NotList items={NDA_NOTS} />
              </div>
            </div>
        </SectionHead>
          </div>
</div>
      </section>

      {/* ── 7. Analytical Boundaries — split provide / not-provide frame ── */}
      <section className="py-16 md:py-20 bg-memo-paper border-b border-memo-line">
        <div className="container-site">
          <div id="delivery-7" data-testid="delivery-section-7">
            <SectionHead num="07" title="Analytical Boundaries" isOpen={openSection === 7} onToggle={() => toggle(7)}>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 overflow-hidden border border-memo-line-2 rounded">
              <div className="p-8 bg-memo-paper border-r border-memo-line">
                <SubHeader title="We provide" />
                <BulletList items={PROVIDE} />
              </div>
              <div className="p-8 bg-memo-paper-2">
                <SubHeader title="We do not provide" muted />
                <NotList items={NOT_PROVIDE} />
              </div>
            </div>
            <div className="mt-7 space-y-2">
              <PolicyLine text="Outputs are analytical artifacts." />
              <PolicyLine text="Operational decisions remain client responsibility." />
            </div>
        </SectionHead>
          </div>
</div>
      </section>

      {/* ── 8. Artifact Delivery Model ── */}
      <section className="py-16 md:py-20 border-b border-memo-line">
        <div className="container-site">
          <div id="delivery-8" data-testid="delivery-section-8">
            <SectionHead num="08" title="Artifact Delivery Model" isOpen={openSection === 8} onToggle={() => toggle(8)}>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-10 items-start">
              <div>
                <p className="text-[0.95rem] leading-relaxed mb-3" style={{ color: 'var(--memo-ink-2)' }}>Deliverables are provided as:</p>
                <BulletList items={ARTIFACTS} />
              </div>
              <div>
                <p className="text-[0.95rem] leading-relaxed mb-3" style={{ color: 'var(--memo-ink-2)' }}>We do not deliver:</p>
                <NotList items={NOT_DELIVER} />
                <p className="mt-7 text-[0.95rem] leading-relaxed" style={{ color: 'var(--memo-ink-2)' }}>
                  Interpretation is performed through structured readouts.
                </p>
              </div>
            </div>
        </SectionHead>
          </div>
</div>
      </section>

      {/* ── 9. Engagement Scope Control ── */}
      <section className="py-16 md:py-20 border-b border-memo-line">
        <div className="container-site">
          <div id="delivery-9" data-testid="delivery-section-9">
            <SectionHead num="09" title="Engagement Scope Control" isOpen={openSection === 9} onToggle={() => toggle(9)}>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-10 items-start">
              <div>
                <p className="text-[0.95rem] leading-relaxed mb-3" style={{ color: 'var(--memo-ink-2)' }}>Each engagement includes:</p>
                <BulletList items={INCLUDES} />
              </div>
              <div>
                <p className="text-[0.95rem] leading-relaxed mb-3" style={{ color: 'var(--memo-ink-2)' }}>Scope expansion requires:</p>
                <BulletList items={SCOPE_EXPANSION} />
              </div>
            </div>
        </SectionHead>
          </div>
</div>
      </section>

      {/* ── 10. Liability Framework — full ledger ── */}
      <section className="py-16 md:py-20 bg-memo-paper border-b border-memo-line">
        <div className="container-site">
          <div id="delivery-10" data-testid="delivery-section-10">
            <SectionHead num="10" title="Liability Framework" isOpen={openSection === 10} onToggle={() => toggle(10)}>
            <div className="mt-8 border border-memo-line-2 rounded overflow-hidden">
              {LIABILITY.map((line) => <p key={line} className="px-5 py-3 border-b border-memo-line last:border-0 text-[0.95rem] leading-snug" style={{ color: 'var(--memo-ink)' }}>{line}</p>)}
            </div>
            <div className="mt-7 space-y-2">
              <PolicyLine text="We isolate structural distortion." />
              <PolicyLine text="Implementation responsibility remains internal." />
            </div>
        </SectionHead>
          </div>
</div>
      </section>

      {/* ── 11. Pricing Architecture — note frame ── */}
      <section className="py-16 md:py-20 border-b border-memo-line">
        <div className="container-site">
          <div id="delivery-11" data-testid="delivery-section-11">
            <SectionHead num="11" title="Pricing Architecture" isOpen={openSection === 11} onToggle={() => toggle(11)}>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-[1fr_1.2fr] gap-10 items-start">
              <div>
                <p className="text-[0.95rem] leading-relaxed mb-3" style={{ color: 'var(--memo-ink-2)' }}>Pricing is based on:</p>
                <BulletList items={PRICING_FACTORS} />
              </div>
              <div className="p-7 border border-memo-line-2 bg-memo-field rounded">
                <SubHeader title="Note" />
                <p className="text-[0.95rem] leading-relaxed mb-3" style={{ color: 'var(--memo-ink-2)' }}>
                  We do not publish fixed pricing tiers. The Diagnostic Session determines:
                </p>
                <BulletList items={DIAGNOSTIC_OUTCOME} />
                <p className="mt-4 text-sm leading-relaxed" style={{ color: 'var(--memo-muted)' }}>
                  Formal proposal follows structural classification.
                </p>
              </div>
            </div>
        </SectionHead>
          </div>
</div>
      </section>

      {/* ── 12. Enterprise Compatibility — because frame ── */}
      <section className="py-16 md:py-20 border-b border-memo-line">
        <div className="container-site">
          <div id="delivery-12" data-testid="delivery-section-12">
            <SectionHead num="12" title="Enterprise Compatibility" isOpen={openSection === 12} onToggle={() => toggle(12)}>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-10 items-start">
              <div>
                <p className="text-[0.95rem] leading-relaxed mb-3" style={{ color: 'var(--memo-ink-2)' }}>The delivery model is compatible with:</p>
                <BulletList items={COMPATIBLE} />
              </div>
              <div className="p-7 border border-memo-line-2 bg-memo-paper rounded">
                <SubHeader title="Because" muted />
                <BulletList items={BECAUSE} />
              </div>
            </div>
        </SectionHead>
          </div>
</div>
      </section>
        </div>
      </div>

      {/* ── Sign-off CTA — paper, hairline-ruled, no arrows ── */}
      <section className="py-24 md:py-32 bg-memo-paper-2 border-t border-memo-line">
        <div className="container-memo">
          <div className="flex flex-wrap gap-4">
            <CtaPrimary to="/intake" testid="delivery-model-intake-cta">Begin Intake</CtaPrimary>
            <CtaGhost to="/procurement">Procurement Pack</CtaGhost>
            <Link
              to="/faq"
              className="inline-flex items-center text-[0.92rem] font-medium text-memo-ink-2 hover:text-memo-accent transition-colors duration-150"
            >
              FAQ / Boundaries
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}